import React, { useState } from 'react';
import { api } from '../data/api';
import { NUTRITION } from '../data/programData';
import { Lightning, UserCircle, ArrowLeft, Check } from '@phosphor-icons/react';

export default function Onboarding({ onComplete, onSwitchToLogin }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    username: '', passcode: '', name: '', weight: '', bf: '', height: '',
    unit: 'metric', cal_plan: 1, start_date: new Date().toISOString().split('T')[0],
    plan_type: '', // 'protocol' | 'custom'
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const payload = {
        username: form.username,
        passcode: form.passcode,
        plan_type: form.plan_type || 'protocol',
        name: form.name || form.username,
        weight: parseFloat(form.weight) || 80,
        bf: parseFloat(form.bf) || 18,
        height: parseFloat(form.height) || 178,
        unit: form.unit,
        cal_plan: form.cal_plan,
        start_date: form.start_date || new Date().toISOString().split('T')[0],
      };
      const res = await api.register(payload);
      localStorage.setItem('leprotocol_token', res.token);
      localStorage.setItem('leprotocol_user', res.user.user_id);
      onComplete({ ...res.user, plan_type: form.plan_type });
    } catch (err) {
      const msg = typeof err.message === 'string' ? err.message : 'Registration failed';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const totalSteps = form.plan_type === 'custom' ? 2 : 3;

  return (
    <div className="min-h-screen bg-base flex items-start justify-center p-4 pt-8 overflow-y-auto" data-testid="onboarding-screen">
      <div className="w-full max-w-md">
        <div className="text-center mb-4">
          <h1 className="font-head text-4xl sm:text-5xl font-black uppercase tracking-tighter text-white">LE PROTOCOL</h1>
          <p className="text-brand font-head text-base sm:text-lg font-bold uppercase tracking-wide mt-1">Create Your Account</p>
        </div>

        <div className="flex gap-2 mb-3 justify-center">
          {Array.from({ length: totalSteps }, (_, i) => (
            <div key={i} className={`h-1.5 w-12 sm:w-16 rounded-full transition-all ${step >= i + 1 ? 'bg-brand' : 'bg-border'}`} />
          ))}
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm p-3 rounded-md mb-3 text-center" data-testid="auth-error">
            {String(error)}
          </div>
        )}

        {/* Step 1: Credentials + Plan Selection */}
        {step === 1 && (
          <div className="glass rounded-xl p-4 sm:p-6 space-y-3">
            <h2 className="font-head text-lg sm:text-xl font-bold uppercase text-white text-center">Select Plan & Create Login</h2>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-500 mb-1 block">Username</label>
              <input type="text" required value={form.username}
                onChange={(e) => setForm(p => ({ ...p, username: e.target.value }))}
                className="w-full bg-black/50 border border-border text-white h-12 sm:h-14 text-base rounded-md px-4 focus:border-brand focus:ring-1 focus:ring-brand outline-none"
                placeholder="Choose a username" data-testid="onboard-username-input" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-500 mb-1 block">Passcode</label>
              <input type="password" required value={form.passcode}
                onChange={(e) => setForm(p => ({ ...p, passcode: e.target.value }))}
                className="w-full bg-black/50 border border-border text-white h-12 sm:h-14 text-base rounded-md px-4 focus:border-brand focus:ring-1 focus:ring-brand outline-none"
                placeholder="Create a passcode (4+ chars)" data-testid="onboard-passcode-input" />
            </div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-500 mt-1">Calorie Plan</div>
            {[
              { plan: 1, data: NUTRITION.opt1 },
              { plan: 2, data: NUTRITION.opt2 },
            ].map(({ plan, data }) => (
              <button key={plan} type="button"
                onClick={() => setForm(p => ({ ...p, cal_plan: plan }))}
                className={`w-full p-3 sm:p-4 rounded-md border-2 text-left transition-all active:scale-[0.98] ${
                  form.cal_plan === plan ? 'border-brand bg-brand/10' : 'border-border bg-elevated'
                }`} data-testid={`onboard-plan-${plan}`}>
                <div className="flex justify-between items-center">
                  <div>
                    <span className="font-head text-sm sm:text-base font-bold uppercase text-white">Option {plan}</span>
                    <span className="text-xs text-neutral-400 ml-2">{data.label}</span>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    form.cal_plan === plan ? 'border-brand bg-brand' : 'border-neutral-600'
                  }`}>{form.cal_plan === plan && <div className="w-2 h-2 bg-white rounded-full" />}</div>
                </div>
              </button>
            ))}
            <button type="button"
              onClick={() => {
                if (!form.username || form.username.length < 3) { setError('Username must be 3+ characters'); return; }
                if (!form.passcode || form.passcode.length < 4) { setError('Passcode must be 4+ characters'); return; }
                setError('');
                setStep(2);
              }}
              className="w-full bg-brand text-white font-head font-bold text-base sm:text-lg uppercase tracking-wide h-12 sm:h-14 rounded-md hover:bg-brand-hover transition-colors active:scale-95"
              data-testid="onboard-next-btn">NEXT</button>
            <button type="button" onClick={onSwitchToLogin}
              className="w-full text-center text-neutral-500 text-sm py-2 hover:text-white transition-colors"
              data-testid="switch-to-login-btn">
              Already have an account? <span className="text-brand font-bold">LOG IN</span>
            </button>
          </div>
        )}

        {/* Step 2: Path Selector */}
        {step === 2 && (
          <div className="space-y-3">
            <h2 className="font-head text-lg sm:text-xl font-bold uppercase text-white text-center mb-1">Choose Your Path</h2>
            <p className="text-xs text-neutral-500 text-center mb-3">Follow our proven protocol or build something custom</p>

            {/* Protocol Option */}
            <button type="button"
              onClick={() => setForm(p => ({ ...p, plan_type: 'protocol' }))}
              className={`w-full rounded-md border-2 p-4 sm:p-5 text-left transition-all active:scale-[0.98] ${
                form.plan_type === 'protocol' ? 'border-brand bg-brand/10' : 'border-border bg-surface'
              }`} data-testid="path-protocol">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-md bg-brand/15 flex items-center justify-center flex-shrink-0">
                  <Lightning size={24} weight="bold" className="text-brand" />
                </div>
                <div className="flex-1">
                  <div className="font-head text-base sm:text-lg font-bold uppercase text-white">The Protocol</div>
                  <div className="text-xs text-neutral-400 mt-0.5 leading-relaxed">
                    48-week Diamond Edition. 4 phases of progressive transformation — Foundation, Mass Build, Power & Sculpt, Peak Shred. Proven structure with exact nutrition.
                  </div>
                </div>
                {form.plan_type === 'protocol' && <Check size={20} weight="bold" className="text-brand flex-shrink-0 mt-1" />}
              </div>
            </button>

            {/* Custom Option */}
            <button type="button"
              onClick={() => setForm(p => ({ ...p, plan_type: 'custom' }))}
              className={`w-full rounded-md border-2 p-4 sm:p-5 text-left transition-all active:scale-[0.98] ${
                form.plan_type === 'custom' ? 'border-accent bg-accent/10' : 'border-border bg-surface'
              }`} data-testid="path-custom">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-md bg-accent/15 flex items-center justify-center flex-shrink-0">
                  <UserCircle size={24} weight="bold" className="text-accent" />
                </div>
                <div className="flex-1">
                  <div className="font-head text-base sm:text-lg font-bold uppercase text-white">Build My Plan</div>
                  <div className="text-xs text-neutral-400 mt-0.5 leading-relaxed">
                    Answer a few questions or upload your own program. AI builds a fully personalized workout, nutrition & warm-up plan tailored to you.
                  </div>
                </div>
                {form.plan_type === 'custom' && <Check size={20} weight="bold" className="text-accent flex-shrink-0 mt-1" />}
              </div>
            </button>

            <div className="pt-2 space-y-2">
              {form.plan_type === 'protocol' && (
                <button type="button" onClick={() => setStep(3)}
                  className="w-full bg-brand text-white font-head font-bold text-base uppercase h-12 sm:h-14 rounded-md active:scale-95 transition-all"
                  data-testid="path-continue-btn">Continue with Protocol</button>
              )}
              {form.plan_type === 'custom' && (
                <button type="button" onClick={() => handleSubmit()} disabled={saving}
                  className="w-full bg-accent text-black font-head font-bold text-base uppercase h-12 sm:h-14 rounded-md active:scale-95 transition-all"
                  data-testid="path-continue-btn">
                  {saving ? 'Creating account...' : 'Create Account & Build Plan'}
                </button>
              )}
              <button type="button" onClick={() => setStep(1)}
                className="w-full h-9 flex items-center justify-center gap-1 text-neutral-500 text-xs font-bold uppercase active:scale-95"
                data-testid="path-back-btn">
                <ArrowLeft size={12} weight="bold" /> Back
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Stats (Protocol path only) */}
        {step === 3 && (
          <form onSubmit={handleSubmit} className="glass rounded-xl p-4 sm:p-6 space-y-3">
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-head text-lg sm:text-xl font-bold uppercase text-white">Your Stats</h2>
              <button type="button" onClick={() => setStep(2)} className="text-xs text-brand font-bold uppercase">Back</button>
            </div>
            <div className="bg-elevated border border-border rounded-md px-3 py-2 flex justify-between items-center">
              <span className="text-xs text-neutral-500 uppercase font-bold">Account</span>
              <span className="font-mono text-sm text-brand font-bold">@{form.username}</span>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-500 mb-1 block">Display Name</label>
              <input type="text" required value={form.name}
                onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                className="w-full bg-black/50 border border-border text-white h-12 sm:h-14 text-base rounded-md px-4 focus:border-brand outline-none"
                placeholder="Your name" data-testid="onboard-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-500 mb-1 block">Weight (kg)</label>
                <input type="number" step="0.1" required value={form.weight}
                  onChange={(e) => setForm(p => ({ ...p, weight: e.target.value }))}
                  className="w-full bg-black/50 border border-border text-white h-12 sm:h-14 text-base rounded-md px-3 focus:border-brand outline-none font-mono"
                  placeholder="92.1" data-testid="onboard-weight-input" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-500 mb-1 block">Body Fat %</label>
                <input type="number" step="0.1" required value={form.bf}
                  onChange={(e) => setForm(p => ({ ...p, bf: e.target.value }))}
                  className="w-full bg-black/50 border border-border text-white h-12 sm:h-14 text-base rounded-md px-3 focus:border-brand outline-none font-mono"
                  placeholder="22.3" data-testid="onboard-bf-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-500 mb-1 block">Height (cm)</label>
                <input type="number" required value={form.height}
                  onChange={(e) => setForm(p => ({ ...p, height: e.target.value }))}
                  className="w-full bg-black/50 border border-border text-white h-12 sm:h-14 text-base rounded-md px-3 focus:border-brand outline-none font-mono"
                  placeholder="185" data-testid="onboard-height-input" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-500 mb-1 block">Start Date</label>
                <input type="date" required value={form.start_date}
                  onChange={(e) => setForm(p => ({ ...p, start_date: e.target.value }))}
                  className="w-full bg-black/50 border border-border text-white h-12 sm:h-14 text-sm rounded-md px-3 focus:border-brand outline-none"
                  data-testid="onboard-date-input" />
              </div>
            </div>
            <button type="submit" disabled={saving}
              className="w-full bg-brand text-white font-head font-bold text-base sm:text-lg uppercase tracking-wide h-12 sm:h-14 rounded-md hover:bg-brand-hover transition-colors active:scale-95"
              data-testid="onboard-submit-btn">
              {saving ? 'CREATING...' : 'BEGIN TRANSFORMATION'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
